import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Ref, ChangeEvent, PropsWithoutRef, DragEvent } from 'react';

import type {
  BaseProps,
  ForwardRefForwardPropsComponent,
  NoChildrenProp,
  OmitStrict,
  TestIdProp
} from '../../types';
import {
  useConsolidatedRef,
  useI18n,
  useUID,
  useTestIds,
  usePrevious,
  useElement,
  useModalManager,
  useModalContext,
  useLiveLog
} from '../../hooks';
import type { FormControlProps } from '../FormControl';
import Flex from '../Flex';
import Text from '../Text';
import Icon, { registerIcon } from '../Icon';
import * as paperClipIcon from '../Icon/icons/paper-clip.icon';
import { getActiveElement, getFocusables, withTestIds, createClassName } from '../../utils';
import Tooltip from '../Tooltip';
import Button from '../Button';
import Modal, { type ModalMethods, type ModalProps } from '../Modal';
import VisuallyHiddenText from '../VisuallyHiddenText';
import MetaList from '../MetaList';

import type { FileItemProps } from './FileItem';
import { getFileInputTestIds } from './File.test-ids';
import FileList from './FileList';
import type { FileListProps } from './FileList';
import FileItem from './FileItem';
import {
  StyledBareButton,
  StyledDropZone,
  StyledFileInput,
  StyledIconWrapper,
  StyledAttachedFileCount,
  StyledSingleFileWrapper,
  StyledUploadStatus,
  StyledFlex,
  StyledFormField,
  StyledFileInputControl,
  StyledDownloadAllButton
} from './FileInput.styles';

registerIcon(paperClipIcon);

/** Props to be used when rendering compact variant for multiple FileInput. */
interface MultiAttachCompactProps {
  /** Modal props */
  modal?: Pick<ModalProps, 'onBeforeOpen'> & {
    /** Callback for cancel button */
    onCancel: ({ close }: { close: () => void }) => void;
    /** Callback for submit button */
    onSubmit: ({ close }: { close: () => void }) => void;
    /** If true renders loading indicator inside Modal. */
    loading?: boolean;
  };
  /** Conveys the number of files attached  */
  count?: number;
}

interface ComponentProps
  extends Pick<BaseFileInputProps, 'compact' | 'multiple' | 'status' | 'readOnly'>,
    Pick<MultiAttachCompactProps, 'count'> {
  dragOver?: boolean;
}

interface BaseFileInputProps
  extends BaseProps,
    NoChildrenProp,
    TestIdProp,
    Pick<FileItemProps, 'previewFromActions'>,
    Pick<FileListProps, 'onDownloadAll'> {
  /** Called when files are added either via the input or drop zone. */
  onFilesAdded?: (files: File[]) => void;
  /**
   * Allow multiple files to be selected from the OS specific file browser.
   * NOTE: This does not restrict multiple files from being added via drag and drop.
   * Restrict multi file drag and drop via onFilesAdded and custom info message.
   * @default false
   */
  multiple?: boolean;
  /**
   * Renders compact variant.
   * @default false
   */
  compact?: boolean;
  /** An array of files that have been uploaded. */
  files?: OmitStrict<FileItemProps, 'previewFromActions'>[];
  /**
   * Specify allowed file types.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept}
   */
  accept?: string | string[];
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /** Set visual state based on a validation state. */
  status?: FormControlProps['status'];
  /** Pass a string or a fragment with an Icon and string. */
  label?: FormControlProps['label'];
  /** Visually hides the label region. */
  labelHidden?: FormControlProps['labelHidden'];
  /** It is recommended to pass a simple string to offer guidance. Text will be styled based on status prop. */
  info?: FormControlProps['info'];
  /** Indicate if the field is required. The browser defaults to false. */
  required?: FormControlProps['required'];
  /** Disable the control. The browser defaults to false. */
  disabled?: FormControlProps['disabled'];
  /** Makes the input non editable and non clickable. */
  readOnly?: FormControlProps['readOnly'];
  /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
  name?: FormControlProps['name'];
  /** Pass a heading and content to show additional information on the field. */
  additionalInfo?: FormControlProps['additionalInfo'];
  /** Ref for the input element within the component's dom structure. */
  ref?: Ref<HTMLInputElement>;
}

export interface FileInputProps extends BaseFileInputProps, MultiAttachCompactProps {}

const Component = ({
  compact,
  multiple,
  status,
  count,
  dragOver = false,
  readOnly
}: ComponentProps) => {
  const t = useI18n();

  const attachedFileCount =
    multiple && count ? (
      <StyledAttachedFileCount>{t('number_of_items', [count], { count })}</StyledAttachedFileCount>
    ) : undefined;

  return (
    <>
      <StyledUploadStatus
        item={{ grow: 1 }}
        container={{ justify: compact && multiple ? 'start' : 'center', alignItems: 'center' }}
        status={status}
        dragOver={dragOver}
        readOnly={readOnly}
      >
        <Text variant='secondary'>
          {compact
            ? attachedFileCount
            : t('file_upload_text_main', [
                t(multiple ? 'file_upload_text_multiple' : 'file_upload_text_one')
              ])}
        </Text>
      </StyledUploadStatus>

      <StyledIconWrapper container={{ justify: 'center', alignItems: 'center' }}>
        <Icon name='paper-clip' />
      </StyledIconWrapper>
    </>
  );
};

const BaseFileInput: ForwardRefForwardPropsComponent<BaseFileInputProps> = forwardRef(
  function FileInput(props: PropsWithoutRef<BaseFileInputProps>, ref: BaseFileInputProps['ref']) {
    const uid = useUID();
    const {
      testId,
      id = uid,
      label,
      labelHidden,
      info,
      required = false,
      disabled = false,
      readOnly = false,
      files,
      onDownloadAll,
      onFilesAdded,
      multiple = false,
      status,
      additionalInfo,
      accept,
      compact = false,
      previewFromActions = false,
      className,
      ...restProps
    } = props;

    const testIds = useTestIds(testId, getFileInputTestIds);
    const [target, setTarget] = useElement<HTMLElement>();

    const inputRef = useConsolidatedRef(ref);
    const fileItemListRef = useRef<HTMLDivElement>();
    const dropZoneRef = useConsolidatedRef(setTarget);
    const activeElement = getActiveElement();
    const activeElementInList = fileItemListRef.current?.contains(activeElement);
    const isDropZoneActiveElement = activeElement === dropZoneRef.current;
    const compactWithSingleAttach = compact && !multiple;

    const filesAnnounced = useRef(false);
    const announcedFilesSet = useRef<Set<string>>(new Set());
    const { announcePolite } = useLiveLog();

    const onChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
          onFilesAdded?.(Array.from(e.target.files));
        }
        /**
         * WHY are we doing this?
         * We are not working with the "value" prop of the input but rather the File instances that are created.
         * The consumer of this component should manage an array or File(s) and allow for a user to remove a selected file.
         * Since we can not modify the input value of files for browser security reasons,
         * and since setting the files property is less than ideal, we reset/toggle the input to a fresh state after a render.
         * This enables onChange to fire even if the user had just previously selected a file, removed it, and selected it again.
         */
        if (inputRef.current) inputRef.current.value = '';
      },
      [onFilesAdded]
    );

    const t = useI18n();
    const [dragOver, setDragOver] = useState(false);
    const hideFileInput = !multiple && files && files.length > 0;

    const fileInput = (
      <StyledFileInput>
        <input
          data-testid={testIds.control}
          {...restProps}
          ref={inputRef}
          type='file'
          disabled={disabled}
          readOnly={readOnly}
          className={createClassName('file-input', className, {
            compact
          })}
          onChange={onChange}
          multiple={multiple}
          accept={Array.isArray(accept) ? accept.join(',') : accept}
        />
        <Flex
          container
          as={StyledDropZone}
          forwardedAs='button'
          type='button'
          id={id}
          required={required}
          aria-describedby={`${id}-file-status${info ? ` ${id}-info` : ''}`}
          disabled={disabled}
          readOnly={readOnly}
          onClick={() => {
            dropZoneRef.current?.focus();
            if (!readOnly) {
              inputRef.current?.click();
            }
          }}
          {...(!disabled && !readOnly
            ? {
                onDragOver: (e: DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                },
                onDragEnter: () => setDragOver(true),
                onDragLeave: () => setDragOver(false),
                onDrop: (e: DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  setDragOver(false);

                  const fileList = Array.from(e.dataTransfer.files);
                  onFilesAdded?.(multiple ? fileList : fileList.slice(0, 1));
                }
              }
            : undefined)}
          compactMode={compact}
          ref={dropZoneRef}
        >
          <Component
            compact={compact}
            multiple={multiple}
            status={status}
            dragOver={dragOver}
            readOnly={readOnly}
          />
        </Flex>
        <VisuallyHiddenText id={`${id}-file-status`}>
          {files && files.length > 0
            ? t('number_of_items', [files.length], { count: files.length })
            : t('no_file_chosen')}
        </VisuallyHiddenText>
        {compactWithSingleAttach && target && (
          <Tooltip target={target} describeTarget>
            {t('file_upload_text_main', [t('file_upload_text_one')])}
          </Tooltip>
        )}
      </StyledFileInput>
    );

    const getReadOnlyFile = (file: FileItemProps) => {
      const readOnlyFile = { ...file };
      delete readOnlyFile.onCancel;
      delete readOnlyFile.onEdit;
      delete readOnlyFile.onDelete;
      delete readOnlyFile.progress;
      return readOnlyFile;
    };

    const fileItemList = hideFileInput ? (
      <StyledSingleFileWrapper
        data-testid={testIds.files}
        compact={compact}
        ref={element => {
          if (element) fileItemListRef.current = element;
        }}
      >
        <FileItem
          {...(readOnly ? getReadOnlyFile(files[0]) : files[0])}
          compact={compact}
          previewFromActions={compactWithSingleAttach && previewFromActions}
          disabled={disabled}
        />
      </StyledSingleFileWrapper>
    ) : (
      <FileList
        type='item'
        items={
          readOnly
            ? files?.map(file => {
                return getReadOnlyFile(file);
              }) ?? []
            : files ?? []
        }
        data-testid={testIds.files}
        disabled={disabled}
        ref={element => {
          if (element) fileItemListRef.current = element;
        }}
      />
    );

    const prevFiles = usePrevious(files);

    useEffect(() => {
      if (
        !multiple &&
        prevFiles?.length === 0 &&
        files?.length === 1 &&
        fileItemListRef.current &&
        isDropZoneActiveElement
      ) {
        getFocusables(fileItemListRef.current)[0]?.focus();
      }

      if (prevFiles?.length === 1 && files?.length === 0 && activeElementInList) {
        dropZoneRef.current?.focus();
      }

      if (!files) return;

      if (announcedFilesSet.current.size !== files.length) {
        filesAnnounced.current = false;
      }
      if ((prevFiles?.length ?? 0) > files.length) {
        announcedFilesSet.current = new Set(
          Array.from(announcedFilesSet.current).filter(item => files.some(file => file.id === item))
        );
      }

      const errorFiles = files.filter(
        item => !announcedFilesSet.current.has(item.id) && item.error
      );
      const progressFiles = files.filter(
        item =>
          !announcedFilesSet.current.has(item.id) &&
          ('progress' in item ? typeof item.progress === 'number' && item.progress > 99 : true)
      );
      const totalAttachedFilesLength = progressFiles.length + errorFiles.length;

      if (
        totalAttachedFilesLength > 0 &&
        files.length - announcedFilesSet.current.size === totalAttachedFilesLength &&
        !filesAnnounced.current
      ) {
        if (progressFiles.length > 0) {
          announcePolite({
            message: t('files_uploaded', [progressFiles.length], { count: progressFiles.length })
          });
          progressFiles.forEach(file => {
            if (file.id && !announcedFilesSet.current.has(file.id)) {
              announcedFilesSet.current.add(file.id);
            }
          });
        }
        if (errorFiles.length > 0) {
          announcePolite({
            message: t('files_upload_failed', [errorFiles.length], { count: errorFiles.length })
          });
          errorFiles.forEach(file => {
            if (file.id && !announcedFilesSet.current.has(file.id)) {
              announcedFilesSet.current.add(file.id);
            }
          });
        }
        filesAnnounced.current = true;
      }
    }, [files]);

    const renderElement = hideFileInput ? fileItemList : fileInput;

    const fileInputInfoActions = useMemo(() => {
      if (info && !onDownloadAll) {
        return info;
      }

      const downloadAllButton = (
        <Button onClick={onDownloadAll} variant='link'>
          {t('download_all')}
        </Button>
      );

      if (onDownloadAll && !info && files && files.length > 1 && multiple) {
        return downloadAllButton;
      }

      if (info && onDownloadAll) {
        if (files && files.length > 1 && multiple) {
          return <MetaList items={[info, downloadAllButton]} />;
        }
        return info;
      }
    }, [info, onDownloadAll]);

    return (
      <Flex
        container={{ rowGap: hideFileInput ? undefined : 1, direction: 'column' }}
        as={StyledFileInputControl}
        status={status}
      >
        {label ? (
          <StyledFormField
            testId={testIds}
            label={
              <>
                {label}
                {readOnly && <VisuallyHiddenText>{t('read_only')}</VisuallyHiddenText>}
              </>
            }
            labelHidden={labelHidden}
            id={id}
            info={fileInputInfoActions}
            required={required}
            disabled={!hideFileInput && disabled}
            readOnly={readOnly}
            status={status}
            additionalInfo={additionalInfo}
            compactMode={compactWithSingleAttach}
          >
            <>
              {renderElement}
              {readOnly && files && files.length > 1 && multiple && (
                <StyledDownloadAllButton variant='link' onClick={onDownloadAll}>
                  {t('download_all')}
                </StyledDownloadAllButton>
              )}
            </>
          </StyledFormField>
        ) : (
          renderElement
        )}
        {multiple && !compact && files && files.length > 0 && fileItemList}
      </Flex>
    );
  }
);

const AttachFilesModal = ({
  modalContent,
  modal,
  readOnly
}: {
  modalContent?: JSX.Element;
  readOnly: FileInputProps['readOnly'];
} & Pick<MultiAttachCompactProps, 'modal'>) => {
  const t = useI18n();
  const { dismiss } = useModalContext();

  const formActions = (
    <>
      <Button
        disabled={modal?.loading}
        onClick={() => {
          if (modal?.onCancel) modal.onCancel({ close: dismiss });
        }}
      >
        {t('cancel')}
      </Button>

      <Button
        disabled={modal?.loading}
        type='submit'
        variant='primary'
        onClick={() => {
          if (modal?.onSubmit) modal.onSubmit({ close: dismiss });
        }}
      >
        {t('submit')}
      </Button>
    </>
  );

  return (
    <Modal
      progress={modal?.loading}
      heading={t('attach_files')}
      actions={!readOnly ? formActions : undefined}
      onBeforeOpen={modal?.onBeforeOpen}
    >
      {modalContent}
    </Modal>
  );
};

const FileInput: ForwardRefForwardPropsComponent<FileInputProps> = forwardRef(function FileInput(
  props: PropsWithoutRef<FileInputProps>,
  ref: FileInputProps['ref']
) {
  const uid = useUID();
  const { modal, count, ...baseFileInputProps } = props;

  const {
    id = uid,
    label,
    labelHidden,
    info,
    required = false,
    disabled = false,
    readOnly = false,
    onFilesAdded,
    multiple = false,
    status,
    additionalInfo,
    compact = false
  } = baseFileInputProps;

  const t = useI18n();
  const [dragOver, setDragOver] = useState(false);
  const [buttonEl, setButtonEl] = useElement<HTMLButtonElement>();
  const { create } = useModalManager();
  const modalMethods = useRef<ModalMethods>();

  let modalContent: JSX.Element | undefined;

  if (compact && multiple) {
    modalContent = (
      <BaseFileInput
        {...baseFileInputProps}
        multiple
        compact={false}
        labelHidden={false}
        ref={ref}
      />
    );
  }

  const modalProps = {
    modal,
    modalContent
  };

  useEffect(() => {
    if (compact && multiple && modal) {
      modalMethods.current?.update({
        modal,
        modalContent,
        readOnly
      });
    }
  }, [compact, multiple, modal, readOnly, modalProps]);

  useEffect(() => {
    return () => {
      if (modalMethods.current) {
        modalMethods.current?.dismiss();
        modalMethods.current?.unmount();
      }
    };
  }, []);

  if (compact && multiple) {
    const render = (
      <Flex container>
        <StyledBareButton
          id={id}
          onClick={() => {
            modalMethods.current = create(
              AttachFilesModal,
              { ...modalProps, readOnly },
              { dismissible: readOnly }
            );
          }}
          aria-label={
            count && count > 0
              ? `${t('attach_review')} ${t('number_of_items', [count], { count })}`
              : undefined
          }
          aria-describedby={`${id}-info`}
          disabled={disabled}
          {...(!disabled && !readOnly
            ? {
                onDragOver: (e: DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                },
                onDragEnter: () => setDragOver(true),
                onDragLeave: () => setDragOver(false),
                onDrop: (e: DragEvent<HTMLDivElement>) => {
                  e.preventDefault();
                  setDragOver(false);
                  onFilesAdded?.(Array.from(e.dataTransfer.files));
                  if (buttonEl) buttonEl.click();
                }
              }
            : undefined)}
          ref={setButtonEl}
        >
          <StyledFlex container>
            <Component
              compact={compact}
              multiple={multiple}
              status={status}
              count={count}
              dragOver={dragOver}
              readOnly={readOnly}
            />
          </StyledFlex>
        </StyledBareButton>
        {buttonEl && (
          <Tooltip target={buttonEl} describeTarget={count === 0}>
            {count && count > 0
              ? t('attach_review')
              : t('file_upload_text_main', [t('file_upload_text_multiple')])}
          </Tooltip>
        )}
      </Flex>
    );

    return label ? (
      <StyledFormField
        label={label}
        labelHidden={labelHidden}
        id={id}
        info={info}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        status={status}
        additionalInfo={additionalInfo}
        compactMode
      >
        {render}
      </StyledFormField>
    ) : (
      render
    );
  }

  return <BaseFileInput {...baseFileInputProps} ref={ref} />;
});

export default withTestIds(FileInput, getFileInputTestIds);
